import { format } from 'date-fns';
import { forwardRef } from 'react';
import QRCode from 'react-qr-code';

const PTPRCertificateTemplate = forwardRef(({ certificate, application, orderOfPayment, hiddenOnPrint = [] }, ref) => {
   // Get certificate data from ptprCertificateData like COV template does
   const certificateData = certificate?.ptprCertificateData;

   console.log('Certificate Data:', certificateData); // For debugging
   console.log('Certificate full object:', certificate); // Add debugging for full certificate

   // Use a direct URL instead of JSON data for better QR code compatibility across devices
   const qrValue = `${window.location.origin}/verify-certificate/${certificate?.id}`;

   // Add date formatting function like in COV template
   const formatDate = (timestamp) => {
      try {
         if (!timestamp) return null;
         if (typeof timestamp === 'string') {
            if (!isNaN(timestamp)) {
               return format(new Date(parseInt(timestamp)), 'MMMM d, yyyy');
            }
            return format(new Date(timestamp), 'MMMM d, yyyy');
         }
         if (typeof timestamp === 'number') {
            return format(new Date(timestamp), 'MMMM d, yyyy');
         }
         if (timestamp instanceof Date) {
            return format(timestamp, 'MMMM d, yyyy');
         }
         return null;
      } catch (error) {
         console.error('Error formatting date:', error, 'timestamp:', timestamp);
         return null;
      }
   };

   // Function to render signature image like in COV template
   const renderSignature = (signatureData) => {
      console.log('Rendering signature with data:', signatureData);
      if (!signatureData?.data) {
         console.log('No signature data available');
         return null;
      }

      console.log('Signature content type:', signatureData.contentType);
      console.log('Signature data length:', signatureData.data.length);

      return (
         <img
            src={`data:${signatureData.contentType};base64,${signatureData.data}`}
            alt="Signature"
            className="h-16 object-contain mb-2"
         />
      );
   };

   return (
      <div ref={ref} className="bg-white p-8 relative">
         {/* Title */}
         <div className="text-center mb-8">
            <h1 className="text-xl font-bold">PRIVATE TREE PLANTATION REGISTRATION</h1>
            <p className="text-lg mt-1">No. {certificate?.certificateNumber || '_________________'}</p>
         </div>

         {/* Owner's Basic Information */}
         <div className="mb-8">
            <h2 className="text-lg font-bold mb-4">Owner's Basic Information</h2>
            <table className="w-full border-collapse">
               <tbody>
                  <tr>
                     <td className="border border-black p-2 w-1/3">Name/ Lot Owner</td>
                     <td className="border border-black p-2">{certificateData?.ownerName || ''}</td>
                  </tr>
                  <tr>
                     <td className="border border-black p-2">Permanent Address</td>
                     <td className="border border-black p-2">{certificateData?.address || ''}</td>
                  </tr>
                  <tr>
                     <td className="border border-black p-2">Contact Number</td>
                     <td className="border border-black p-2">{certificateData?.contactNumber || ''}</td>
                  </tr>
                  <tr>
                     <td className="border border-black p-2">Email Address</td>
                     <td className="border border-black p-2">{certificateData?.email || 'N/A'}</td>
                  </tr>
               </tbody>
            </table>
         </div>

         {/* Tree Plantation Location */}
         <div className="mb-8">
            <h2 className="text-lg font-bold mb-4">Tree Plantation Location</h2>
            <table className="w-full border-collapse">
               <tbody>
                  <tr>
                     <td className="border border-black p-2 w-1/3">OCT/TCT No</td>
                     <td className="border border-black p-2" colSpan="3">{certificateData?.octTctNumber || ''}</td>
                  </tr>
                  <tr>
                     <td className="border border-black p-2">Lot Number</td>
                     <td className="border border-black p-2" colSpan="3">{certificateData?.lotNumber || ''}</td>
                  </tr>
                  <tr>
                     <td className="border border-black p-2">Barangay/s</td>
                     <td className="border border-black p-2" colSpan="3">{certificateData?.barangay || ''}</td>
                  </tr>
                  <tr>
                     <td className="border border-black p-2">Municipality/ies</td>
                     <td className="border border-black p-2" colSpan="3">{certificateData?.municipality || ''}</td>
                  </tr>
                  <tr>
                     <td className="border border-black p-2">Province</td>
                     <td className="border border-black p-2" colSpan="3">{certificateData?.province || ''}</td>
                  </tr>
                  <tr>
                     <td className="border border-black p-2">Total Lot Area</td>
                     <td className="border border-black p-2">
                        {certificateData?.totalLotArea ? `${certificateData.totalLotArea}has` : ''}
                     </td>
                     <td className="border border-black p-2">Total Lot Area Devoted to Plantation</td>
                     <td className="border border-black p-2">
                        {certificateData?.areaDevotedToPlantation ? `${certificateData.areaDevotedToPlantation}has` : ''}
                     </td>
                  </tr>
                  <tr>
                     <td className="border border-black p-2">Spacing</td>
                     <td className="border border-black p-2" colSpan="3">{certificateData?.spacing || ''}</td>
                  </tr>
                  <tr>
                     <td className="border border-black p-2">Tree Species planted</td>
                     <td className="border border-black p-2">{certificateData?.treeSpeciesPlanted || ''}</td>
                     <td className="border border-black p-2">Date Planted:</td>
                     <td className="border border-black p-2">{formatDate(certificateData?.datePlanted) || ''}</td>
                  </tr>
               </tbody>
            </table>
         </div>

         {/* Attachments */}
         <div className="mb-8">
            <h2 className="text-lg font-bold mb-4">Attachments</h2>
            <table className="w-full border-collapse">
               <tbody>
                  <tr>
                     <td className="border border-black p-2 w-16 text-center font-bold">X</td>
                     <td className="border border-black p-2">Copy of OCT/TCT/ Tax Declaration</td>
                  </tr>
                  <tr>
                     <td className="border border-black p-2 w-16 text-center font-bold">X</td>
                     <td className="border border-black p-2">GIS Generated Map of Tree Plantation</td>
                  </tr>
               </tbody>
            </table>
         </div>

         {/* Certification */}
         <div className="mb-8">
            <p className="mb-4">I hereby certify that the above information are true and correct.</p>
            <div className="flex justify-between items-center mt-8">
               <div className="text-center">
                  <div className="flex justify-center mb-2">
                     {renderSignature(certificate?.applicantSignature)}
                  </div>
                  <p className="font-bold mb-2">{certificateData?.ownerName?.toUpperCase() || '_______________'}</p>
                  <div className="border-t border-black w-64"></div>
                  <p>Name of the Applicant</p>
               </div>
               <div className="text-center">
                  <p className="mb-2">{formatDate(certificate?.dateIssued) || 'Date'}</p>
                  <div className="border-t border-black w-64"></div>
                  <p>Date Submitted</p>
               </div>
            </div>
         </div>

         {/* Approval */}
         <div className="mt-12">
            <p className="mb-8">I hereby certify that the above information are verified and the Private Tree Plantation registration is approved:</p>
            <div className="flex justify-between items-center mt-8">
               <div className="text-center">
                  <div className="flex justify-center mb-2">
                     {renderSignature(certificate?.penrCenrOfficerSignature)}
                  </div>
                  <p className="font-bold mb-2">IMELDA M. DIAZ</p>
                  <div className="border-t border-black w-64"></div>
                  <p>OIC- PENR Officer</p>
               </div>
               <div className="text-center">
                  <p className="mb-2">{formatDate(certificate?.dateApproved) || ''}</p>
                  <div className="border-t border-black w-64"></div>
                  <p>Date Approved</p>
               </div>
            </div>
         </div>

         {/* QR Code */}
         <div className={`absolute top-4 right-4 ${hiddenOnPrint.includes('qr-code') ? 'no-print' : ''}`}>
            <QRCode
               value={qrValue}
               size={80}
               style={{ height: "auto", maxWidth: "100%", width: "100%" }}
               viewBox={`0 0 80 80`}
            />
            <p className="text-xs mt-1 text-center">Scan to verify</p>
         </div>
      </div>
   );
});

PTPRCertificateTemplate.displayName = 'PTPRCertificateTemplate';

export default PTPRCertificateTemplate;
